import React, { useState, useRef, useEffect, useCallback } from 'react';

import { INotificationHistory } from 'redux/notifications/reducer';
import { IUserRegistrartionSteps } from 'redux/users/types';
import withDropdownNotificationsLogic from "./withDropdownNotifications/withDropdownNotificationsLogic";
import { ModalBasic } from 'components/commons/Modals';
import Notification from '../Notification/Notification';
import type { IHistoryCurrentNotification } from 'redux/notifications/reducer';
import NotificationInfo from './NotificationInfo';
import ComponentsLoader from 'components/ComponentsLoader/ComponentsLoader';
import { t } from 'i18next';
import { Link } from 'react-router-dom';
import OrderNotificationInfo from "./OrderNotificationInfo";
import NotificationViewActions from "../../Task/NotificationViewActions/NotificationViewActions";
import {useAppSelector} from "../../../redux/hooks";
import { ReactComponent as NotificationsIcon } from "assets/icons/new/Profile/Notifications.svg";
import { ReactComponent as ShowListIcon } from "assets/icons/new/UI/ShowList.svg";

export interface IDropdownNotificationsProps {
  notifications: INotificationHistory[] | null,
  isLoading?: boolean,
  error: string | null,
  isNotificationRead: boolean,
  updateNotificationReadStatus: () => void,
  registrationSteps: {data: IUserRegistrartionSteps | null},
  getCurrentNotificationHanlder: (id: number) => void,
  currentNotification: IHistoryCurrentNotification,
  sellerId: number | undefined
}

const DropdownNotifications: React.FC<IDropdownNotificationsProps> = function({
  notifications,
  isNotificationRead,
  updateNotificationReadStatus,
  registrationSteps,
  getCurrentNotificationHanlder,
  currentNotification,
  sellerId
}) {
  const { userId } = useAppSelector(({ users }) => ({
    userId: users.currentUser.info.id,
  }));

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentNotificationModalOpen, setCurrentNotificationModalOpen] = useState<boolean>(false);
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({ position: 'fixed' });

  const btnDropdown = useRef<HTMLDivElement | null>(null);
  const dropdown = useRef<HTMLDivElement | null>(null);
  const notificationModalWrapper = useRef<HTMLDivElement | null>(null);

  const computePopupStyle = useCallback(() => {
    if (!btnDropdown.current) return;
    const rect = btnDropdown.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const popupWidth = Math.min(320, vw - 16);
    const isAtBottom = rect.top > vh / 2;

    const style: React.CSSProperties = { position: 'fixed', width: popupWidth };

    if (isAtBottom) {
      style.bottom = vh - rect.top + 8;
    } else {
      style.top = rect.bottom + 4;
    }

    // центрируем по иконке, зажимаем в границы вьюпорта
    const bellCenter = (rect.left + rect.right) / 2;
    const idealLeft = bellCenter - popupWidth / 2;
    style.left = Math.max(8, Math.min(idealLeft, vw - popupWidth - 8));

    setPopupStyle(style);
  }, []);

  // пересчёт при resize когда открыто
  useEffect(() => {
    if (!dropdownOpen) return;
    window.addEventListener('resize', computePopupStyle);
    return () => window.removeEventListener('resize', computePopupStyle);
  }, [dropdownOpen, computePopupStyle]);

  // close on click outside
  useEffect(() => {
    const clickHandler = (e: MouseEvent) => {
      if (!dropdownOpen) return;
      if (!btnDropdown.current) return;
      if (btnDropdown.current?.contains(e.target as HTMLInputElement)) return;
      if (!dropdown.current) return;
      if (dropdown.current.contains(e.target as HTMLInputElement)) return;
      if(e.composedPath()?.includes(notificationModalWrapper.current as HTMLDivElement)) return;
      setDropdownOpen(false);
      setShowDropdown(false)
    };
    document.addEventListener('mousedown', clickHandler);
    return () => document.removeEventListener('mousedown', clickHandler);
  }, [dropdownOpen]);

  const showNotificationsHandler = () => {
    if (!dropdownOpen) computePopupStyle();
    setDropdownOpen(prev => !prev);
    setShowDropdown(false)
    updateNotificationReadStatus();
  }

  const openCurrentNotificationHandler = (id: number) => {
    getCurrentNotificationHanlder(id);
    setShowDropdown(false)
    setCurrentNotificationModalOpen(true);
  }

  if (registrationSteps && !registrationSteps.data) {
    return null
  }

  return (
    <div className='h-5'>
      <div className="hs-dropdown [--auto-close:inside] [--placement:bottom-right] h-5 relative inline-flex" >
        <div className="hs-tooltip [--placement:bottom] inline-block" ref={btnDropdown}>
          <button
            id="hs-pro-dnnd"
            type="button"
            className="hs-tooltip-toggle relative h-full inline-flex justify-center items-center rounded-full disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden"
            aria-haspopup="menu"
            aria-expanded="false"
            aria-label="Dropdown"
            onClick={showNotificationsHandler}
          >
            <NotificationsIcon width="20" height="20" fill="currentColor" className={`shrink-0 hover:text-theme-hover dark:hover:text-theme-hover`} />
            {!isNotificationRead && notifications && notifications.length > 0 && 
              <span className="flex absolute top-0 end-0 z-10 -mt-[3px] -me-[11px]">
                <span className="animate-ping absolute inline-flex size-full rounded-full bg-red-400 opacity-75 dark:bg-red-600"></span>
                <span className="relative min-w-4.5 min-h-4.5 inline-flex justify-center items-center text-[10px] bg-red-500 text-white rounded-full px-1">
                  {notifications.length}
                </span>
              </span>
            }
          </button>
        </div>
        <div
          ref={dropdown}
          style={popupStyle}
          className={`transition-opacity duration-200 z-[200] shadow-xl ${dropdownOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}

        >
          {/* Шапка - абсолютно спозиционирована над контентом */}
          <div className="relative flex items-stretch">
            {/* Кнопка с выпадающим списком */}
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className={`flex items-center gap-1.5 text-sm p-1 rounded-tl-lg border border-theme-border dark:border-theme-border bg-theme-bg dark:bg-theme-bg`}
              >
                Уведомления
                <ShowListIcon 
                  width="24" 
                  height="24" 
                  fill="currentColor" 
                  className="size-3.5"
                />
              </button>
              
              {/* Выпадающий список */}
              {/* {showDropdown && (
                <div className={`absolute top-full left-0 mt-[-1px] w-full border border-theme-border dark:border-theme-border bg-theme-bg dark:bg-theme-bg rounded-b-lg shadow-lg z-30`}>
                  <ul className="py-1 text-sm">
                    <li className={`px-3 py-2 dark:hover:text-theme-color-active hover:bg-theme-hover dark:hover:bg-theme-hover hover:text-theme-color-active`}>Фильтр 1</li>
                  </ul>
                </div>
              )} */}
            </div>
            
            {/* Блоки справа */}
            {/* <div className={`flex h-auto overflow-x-auto rounded-tr-lg border-t border-theme-border dark:border-theme-border bg-theme-bg dark:bg-theme-bg`}>
              {['wb', 'ozon', 'Я'].map((item, index) => (
                <div 
                  key={index}
                  className={`shrink-0 w-6 h-full rounded-tr-lg border-r border-theme-border dark:border-theme-border flex items-center justify-center text-[10px] font-medium`}
                >
                  {item}
                </div>
              ))}
            </div> */}
          </div>

          {/* Основной контент - смещен вниз от шапки */}
          <div className={`relative z-10 rounded-lg rounded-tl-none border border-theme-border dark:border-theme-border bg-theme-bg dark:bg-theme-bg`}>
            <div id="hs-pro-tabs-dnn-all" role="tabpanel" aria-labelledby="hs-pro-tabs-dnn-item-all">
            <div className="max-h-120 overflow-y-auto overflow-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-track]:bg-neutral-700 dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500">
              <ul className={`divide-y divide-theme-border dark:divide-theme-border`}>
                {notifications?.map((item) => (
                  <Notification 
                    key={item.id}
                    notification={item}
                    openModalHandler={() => openCurrentNotificationHandler(item.id)}
                  />
                ))}
              </ul>
            </div>
            {
              sellerId &&
              <div className={`border-t rounded-b-lg border-theme-border dark:border-theme-border hover:bg-theme-hover dark:hover:bg-theme-hover hover:text-theme-color-active dark:hover:text-theme-color-active`}>
                <Link
                  target="_blank"
                  className={`flex justify-center p-2 text-sm font-medium rounded-b-lg focus:outline-hidden hover:bg-theme-hover dark:hover:bg-theme-hover`}
                  to={`/${sellerId}/notifications`}
                >
                  {t<string>("All notifications")} {'->'}
                </Link>
              </div>
            }
          </div>
          </div>
        </div>
        {/* <div
          ref={dropdown}
          className={`absolute w-80 transition-[opacity,margin] rounded-[10px] duration z-10 rounded-lg shadow-xl border border-theme-border dark:border-theme-border bg-theme-bg dark:bg-theme-bg sm:top-full max-sm:bottom-full right-0 lg:left-0 ${dropdownOpen ? 'opacity-100' : 'opacity-0'}`}
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="hs-pro-dnnd"
        >
          <div id="hs-pro-tabs-dnn-all" role="tabpanel" aria-labelledby="hs-pro-tabs-dnn-item-all">
            <div className="max-h-120 overflow-y-auto overflow-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-track]:bg-neutral-700 dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500">
              <ul className={`divide-y divide-theme-border dark:divide-theme-border`}>
                {notifications?.map((item) => (
                  <Notification 
                    key={item.id}
                    notification={item}
                    openModalHandler={() => openCurrentNotificationHandler(item.id)}
                  />
                ))}
              </ul>
            </div>
            {
              sellerId &&
              <div className={`border-t rounded-b-lg border-theme-border dark:border-theme-border hover:bg-theme-hover dark:hover:bg-theme-hover hover:text-theme-color-active dark:hover:text-theme-color-active`}>
                <Link
                  target="_blank"
                  className={`flex justify-center p-2 text-sm font-medium rounded-b-lg focus:outline-hidden hover:bg-theme-hover dark:hover:bg-theme-hover`}
                  to={`/${sellerId}/notifications`}
                >
                  {t<string>("All notifications")} {'->'}
                </Link>
              </div>
            }
          </div>
        </div> */}
      </div>
      <div ref={notificationModalWrapper}>
        <ModalBasic
          modalOpen={currentNotificationModalOpen}
          setModalOpen={setCurrentNotificationModalOpen}
          id="history-notification-modal"
          title={`${t(`Task status`)} ${currentNotification.data?.id ? currentNotification.data?.id : "..."}`}
        >
          <div className='px-5 py-3 min-h-[82px]'>
            {
              currentNotification.isLoading ?
                <ComponentsLoader />
              : <div>
                    {
                      currentNotification.error ?
                          <div className='text-red-500 text-center'>{currentNotification.error}</div>
                          : currentNotification.data &&
                          <div>
                            {
                              currentNotification.data.type === 11
                                  ?
                                  <OrderNotificationInfo currentNotification={currentNotification} data={currentNotification.data} />
                                  :
                                  <NotificationInfo
                                      currentNotification={currentNotification}
                                      result={currentNotification.data.result}
                                      error={currentNotification.data.error}
                                  />
                            }
                          </div>
                    }

                    <pre className='dev-only border-t-[1px] max-h-48 mt-3 overflow-auto pt-2 text-xs' style={{display: ([1, 382].includes(userId ?? 0) ? "block" : "none"), whiteSpace: 'break-spaces'}}>
                        {JSON.stringify(currentNotification.data, undefined, 4)}
                    </pre>

                </div>
            }
          </div>

          <div className="flex flex-wrap justify-start space-x-2 border-t border-gray-200 dark:border-c-gray px-5 py-3">
            <NotificationViewActions notification={currentNotification}></NotificationViewActions>
          </div>

        </ModalBasic>
      </div>
    </div>
  );
}

export default withDropdownNotificationsLogic(DropdownNotifications);